import { CdkDrag, CdkDragMove, CdkDragRelease } from '@angular/cdk/drag-drop';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Wk8TutorService } from '@core/wk8/wk8-tutor.service';
import { Wk8AudioService } from '@features/wk8/core/wk8-audio/wk8-audio.service';
import { Wk8CeilingComponent } from '@features/wk8/core/wk8-ceiling/wk8-ceiling.component';
import { Wk8ClawBackComponent } from '@features/wk8/core/wk8-claw-back/wk8-claw-back.component';
import { Wk8ClawFrontComponent } from '@features/wk8/core/wk8-claw-front/wk8-claw-front.component';
import { Wk8ConveyorBeltComponent } from '@features/wk8/core/wk8-conveyor-belt/wk8-conveyor-belt.component';
import { Wk8PanelImageComponent } from '@features/wk8/core/wk8-panel-image/wk8-panel-image.component';
import { Wk8PaperPieceComponent } from '@features/wk8/core/wk8-paper-piece/wk8-paper-piece.component';
import { Wk8PipeBackComponent } from '@features/wk8/core/wk8-pipe-back/wk8-pipe-back.component';
import { Wk8PipeFrontComponent } from '@features/wk8/core/wk8-pipe-front/wk8-pipe-front.component';
import { Wk8ReadFocusDirective } from '@features/wk8/core/wk8-read-focus/wk8-read-focus.directive';
import { Wk8ScreenComponent } from '@features/wk8/core/wk8-screen/wk8-screen.component';
import { waitForMillis } from '@shared/delay.utils';
import { sampleRandom, shuffle } from '@shared/misc.utils';
import { WordToKlankConverter } from '@shared/woordkr8/WordToKlankConverter';
import { LpWoordData } from '@typings/lp/generated';

// ========================================
// TYPES & INTERFACES
// ========================================

type DragMode = 'idle' | 'dragging' | 'hovering-replace' | 'hovering-insert' | 'dragging-removal' | 'hovering-cancel-removal';
type KlankAnimationState =
  | 'normal'
  | 'dragging'
  | 'disabled'
  | 'comitted'
  | 'hidden'
  | 'preview'
  | 'highlight'
  | 'being-replaced'
  | 'being-removed'
  | 'inserting'
  | 'waiting-to-exit'
  | 'exiting'
  | 'completing';
type GamePhase = 'playing' | 'completed';
type DragSource = 'air' | 'conveyor';

interface AnimationConfig {
  readonly duration: number;
  readonly easing: string;
  readonly delay?: number;
}

interface AnimationState {
  readonly current: KlankAnimationState;
  readonly previous: KlankAnimationState;
  readonly timestamp: number;
}

interface Position {
  readonly x: number;
  readonly y: number;
}

interface KlankData {
  readonly uid: string;
  readonly klank: string;
  clawOpen: boolean;
  animationState?: AnimationState;
}

interface ViewKlankData extends KlankData {
  readonly isPreview: boolean;
  readonly isBeingReplaced: boolean;
  readonly isBeingRemoved: boolean;
  readonly computedAnimationState: KlankAnimationState;
  readonly animationConfig: AnimationConfig;
}

interface ExitingKlankData extends KlankData {
  readonly originalIndex: number;
  readonly exitAnimationState: 'waiting-to-exit' | 'exiting';
}

interface DragInteractionState {
  readonly mode: DragMode;
  readonly targetIndex: number;
  readonly position: Position;
  readonly activeDraggableIndex: number;
  readonly dragSource: DragSource;
  readonly removalTargetIndex: number;
}

interface InsertionZone {
  readonly left: number;
  readonly right: number;
  readonly y: number;
  readonly index: number;
}

interface GameState {
  readonly currentWordIndex: number;
  readonly phase: GamePhase;
  readonly klankenOnConveyor: KlankData[];
  readonly klankenInAir: KlankData[];
  readonly exitingKlanken: ExitingKlankData[];
  readonly originalKlankenOnConveyor: KlankData[];
  readonly originalKlankenInAir: KlankData[];
  readonly hasPendingMutation: boolean;
}

interface DragDetectionOptions {
  readonly margin: number;
}

// ========================================
// CONSTANTS
// ========================================

const DEFAULT_DRAG_MARGIN = 0;
const KLANK_HEIGHT = 128;
const INSERTION_ZONE_WIDTH = 64;
const DEFAULT_CLAW_SIZE_MULTIPLIER = 1;
const DEFAULT_FONT_SIZE = 64;

// Animation configurations
const ANIMATION_CONFIGS: Record<KlankAnimationState, AnimationConfig> = {
  normal: { duration: 300, easing: 'ease-in-out' },
  dragging: { duration: 200, easing: 'ease-out' },
  disabled: { duration: 300, easing: 'ease-in-out' },
  comitted: { duration: 300, easing: 'ease-in-out' },
  hidden: { duration: 200, easing: 'ease-in' },
  preview: { duration: 400, easing: 'ease-in-out' },
  highlight: { duration: 300, easing: 'ease-out' },
  'being-replaced': { duration: 300, easing: 'ease-in' },
  'being-removed': { duration: 300, easing: 'ease-out' },
  inserting: { duration: 500, easing: 'ease-out' },
  'waiting-to-exit': { duration: 300, easing: 'ease-out' },
  exiting: { duration: 1000, easing: 'ease-out' },
  completing: { duration: 2000, easing: 'ease-in' },
};

@Component({
  selector: 'wk8-woordpad',
  imports: [
    Wk8PaperPieceComponent,
    Wk8ClawFrontComponent,
    Wk8ScreenComponent,
    Wk8ConveyorBeltComponent,
    Wk8CeilingComponent,
    Wk8ClawBackComponent,
    Wk8PipeFrontComponent,
    Wk8PipeBackComponent,
    CdkDrag,
    Wk8ReadFocusDirective,
    Wk8PanelImageComponent,
    NgClass,
    NgTemplateOutlet,
  ],
  templateUrl: './wk8-woordpad.component.html',
  styleUrl: './wk8-woordpad.component.css',
})
export class Wk8WoordpadComponent implements OnInit, AfterViewInit {
  // ========================================
  // PUBLIC API
  // ========================================

  readonly path = input.required<LpWoordData[]>();
  readonly scaleMultiplier = input(1);
  readonly finished = output();

  // ========================================
  // VIEW REFERENCES
  // ========================================

  @ViewChild('panelImage') panelImageComp!: Wk8PanelImageComponent;
  @ViewChild('mechanic') mechanicContainer!: ElementRef<HTMLElement>;
  @ViewChildren('conveyorKlank') conveyorKlanken!: QueryList<ElementRef>;

  // ========================================
  // SERVICES
  // ========================================

  readonly #audioService = inject(Wk8AudioService);
  readonly #tutorService = inject(Wk8TutorService);

  // ========================================
  // CORE STATE
  // ========================================

  readonly #gameState = signal<GameState>({
    currentWordIndex: 0,
    phase: 'playing',
    klankenOnConveyor: [],
    klankenInAir: [],
    exitingKlanken: [],
    originalKlankenOnConveyor: [],
    originalKlankenInAir: [],
    hasPendingMutation: false,
  });

  readonly #dragState = signal<DragInteractionState>({
    mode: 'idle',
    targetIndex: -1,
    position: { x: 0, y: 0 },
    activeDraggableIndex: -1,
    dragSource: 'air',
    removalTargetIndex: -1,
  });

  // Animation state tracking
  readonly #klankAnimationStates = new Map<string, AnimationState>();

  // ========================================
  // LAYOUT STATE
  // ========================================

  #conveyorKlankRects: DOMRect[] = [];
  #insertionZones: InsertionZone[] = [];

  // ========================================
  // COMPUTED PROPERTIES - CORE STATE ACCESS
  // ========================================

  // Simplified state access - use signals directly instead of creating redundant computeds
  readonly gameState = this.#gameState.asReadonly();
  readonly dragState = this.#dragState.asReadonly();

  // Essential computed properties only
  readonly hasWonGame = computed(() => this.gameState().phase === 'completed');
  readonly currentWord = computed(() => this.path()[this.gameState().currentWordIndex]);
  readonly nextWord = computed(() => this.path()[this.gameState().currentWordIndex + 1]);
  readonly screenNumCompleted = computed(() => this.gameState().currentWordIndex);
  readonly screenNumSteps = computed(() => this.path().length - 1);
  readonly hasPendingMutation = computed(() => this.gameState().hasPendingMutation);

  readonly activeDraggableKlank = computed(() => {
    const dragState = this.dragState();
    const index = dragState.activeDraggableIndex;
    const source = dragState.dragSource;

    if (index < 0) return undefined;

    const klanken = source === 'air' ? this.gameState().klankenInAir : this.gameState().klankenOnConveyor;
    return index < klanken.length ? klanken[index] : undefined;
  });

  // ========================================
  // COMPUTED PROPERTIES - VIEW MODEL
  // ========================================

  readonly viewConveyorKlanken = computed((): ViewKlankData[] => {
    const gameState = this.gameState();
    const baseItems = [...gameState.klankenOnConveyor];
    const dragState = this.dragState();

    // For insert mode, show preview in the list
    if (dragState.mode === 'hovering-insert' && dragState.targetIndex >= 0 && dragState.dragSource === 'air') {
      const activeItem = this.activeDraggableKlank();
      if (activeItem) {
        baseItems.splice(dragState.targetIndex, 0, activeItem);
      }
    }

    // For replace mode, insert the preview item right after the target
    // This gives a clear visual of what the list will look like after replacement
    if (dragState.mode === 'hovering-replace' && dragState.targetIndex >= 0 && dragState.dragSource === 'air') {
      const activeItem = this.activeDraggableKlank();
      if (activeItem) {
        baseItems.splice(dragState.targetIndex + 1, 0, activeItem);
      }
    }

    return baseItems
      .map((klankData, index): ViewKlankData => {
        const isInsertPreview =
          dragState.mode === 'hovering-insert' &&
          dragState.targetIndex >= 0 &&
          index === dragState.targetIndex &&
          dragState.dragSource === 'air';

        const isReplacePreview =
          dragState.mode === 'hovering-replace' &&
          dragState.targetIndex >= 0 &&
          index === dragState.targetIndex + 1 &&
          dragState.dragSource === 'air';

        const isReplaceTarget = dragState.mode === 'hovering-replace' && index === dragState.targetIndex && dragState.dragSource === 'air';

        const isBeingRemoved = dragState.mode === 'dragging-removal' && dragState.removalTargetIndex === index;

        const computedAnimationState = this.#computeKlankAnimationState(klankData, {
          isInsertPreview,
          isReplacePreview,
          isReplaceTarget,
          isBeingRemoved,
          hasPendingMutation: gameState.hasPendingMutation,
          gamePhase: gameState.phase,
        });

        return {
          ...klankData,
          isPreview: isInsertPreview || isReplacePreview,
          isBeingReplaced: isReplaceTarget,
          isBeingRemoved,
          computedAnimationState,
          animationConfig: ANIMATION_CONFIGS[computedAnimationState],
        };
      })
      .filter((klankData, index) => {
        // Filter out elements that are being replaced - they should render in exitingKlanken instead
        const isReplacedElement =
          dragState.mode === 'hovering-replace' && index === dragState.targetIndex && dragState.dragSource === 'air';
        // Don't filter out elements being removed - we want to show them with visual feedback
        return !isReplacedElement;
      });
  });

  readonly viewAirKlanken = computed((): ViewKlankData[] => {
    const gameState = this.gameState();
    const dragState = this.dragState();

    return gameState.klankenInAir.map((klankData, index): ViewKlankData => {
      const isActiveDrag = dragState.activeDraggableIndex === index && dragState.dragSource === 'air';
      const isBeingDragged =
        isActiveDrag && (dragState.mode === 'dragging' || dragState.mode === 'hovering-replace' || dragState.mode === 'hovering-insert');
      const shouldHide = isActiveDrag && (dragState.mode === 'hovering-replace' || dragState.mode === 'hovering-insert');

      let computedAnimationState: KlankAnimationState;
      if (shouldHide) {
        computedAnimationState = 'hidden';
      } else if (isBeingDragged) {
        computedAnimationState = 'dragging';
      } else if (gameState.hasPendingMutation) {
        computedAnimationState = 'disabled';
      } else if (gameState.phase === 'completed') {
        computedAnimationState = 'completing';
      } else {
        computedAnimationState = 'normal';
      }

      return {
        ...klankData,
        isPreview: false,
        isBeingReplaced: false,
        isBeingRemoved: false,
        computedAnimationState,
        animationConfig: ANIMATION_CONFIGS[computedAnimationState],
      };
    });
  });

  readonly viewExitingKlanken = computed((): ExitingKlankData[] => {
    const gameState = this.gameState();
    const dragState = this.dragState();
    const exitingFromState = [...gameState.exitingKlanken];

    // Add currently being replaced element if in replace mode (waiting state)
    if (dragState.mode === 'hovering-replace' && dragState.targetIndex >= 0 && dragState.dragSource === 'air') {
      const baseItems = gameState.klankenOnConveyor;
      const targetElement = baseItems[dragState.targetIndex];
      if (targetElement) {
        exitingFromState.push({
          ...targetElement,
          originalIndex: dragState.targetIndex,
          exitAnimationState: 'waiting-to-exit' as const,
        });
      }
    }

    return exitingFromState;
  });

  readonly clawSize = computed(() => DEFAULT_CLAW_SIZE_MULTIPLIER * this.scaleMultiplier());
  readonly fontSize = computed(() => DEFAULT_FONT_SIZE * this.scaleMultiplier());

  readonly submitButtonState = computed(() => {
    if (this.hasWonGame()) return 'disabled';
    return this.gameState().hasPendingMutation ? 'default' : 'disabled';
  });

  readonly swapButtonState = computed(() => {
    return this.gameState().hasPendingMutation ? 'default' : 'disabled';
  });

  // ========================================
  // LIFECYCLE HOOKS
  // ========================================

  async ngOnInit(): Promise<void> {
    await this.#initializeGame();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.#updateConveyorLayoutInfo(), 0);
  }

  // ========================================
  // PUBLIC EVENT HANDLERS
  // ========================================

  startDraggingKlank(index: number, event: CdkDragMove): void {
    // Prevent starting drag if there's already a pending mutation
    if (this.gameState().hasPendingMutation) {
      return;
    }

    const klankData = this.gameState().klankenInAir[index];
    if (klankData) {
      this.#setKlankAnimationState(klankData.uid, 'dragging');
    }

    this.#updateDragState({
      mode: 'dragging',
      activeDraggableIndex: index,
      dragSource: 'air',
    });
    this.dragMoved(event);
  }

  startDraggingConveyorKlank(index: number, event: CdkDragMove): void {
    // Prevent starting drag if there's already a pending mutation
    if (this.gameState().hasPendingMutation) {
      return;
    }

    const klankData = this.gameState().klankenOnConveyor[index];
    if (klankData) {
      this.#setKlankAnimationState(klankData.uid, 'being-removed');
    }

    this.#updateDragState({
      mode: 'dragging-removal',
      activeDraggableIndex: index,
      dragSource: 'conveyor',
      removalTargetIndex: index,
    });
    this.dragMovedRemoval(event);
  }

  dragMoved(event: CdkDragMove): void {
    const containerRelativePointer = this.#getContainerRelativePosition(event.pointerPosition);

    // Check for insertion zones first
    const insertionZone = this.#findInsertionZone(containerRelativePointer);
    if (insertionZone) {
      this.#updateDragState({
        mode: 'hovering-insert',
        targetIndex: insertionZone.index,
        position: { x: insertionZone.left, y: insertionZone.y },
      });
      return;
    }

    // Check for replacement zones
    const replaceIndex = this.#findReplaceTarget(event.pointerPosition);
    if (replaceIndex >= 0) {
      const containerRect = this.mechanicContainer.nativeElement.getBoundingClientRect();
      const klankRect = this.#conveyorKlankRects[replaceIndex];

      this.#updateDragState({
        mode: 'hovering-replace',
        targetIndex: replaceIndex,
        position: {
          x: klankRect.left - containerRect.left,
          y: klankRect.top - containerRect.top,
        },
      });
      return;
    }

    // Default dragging state
    this.#updateDragState({ mode: 'dragging' });
  }

  dragMovedRemoval(event: CdkDragMove): void {
    const containerRelativePointer = this.#getContainerRelativePosition(event.pointerPosition);
    const removalIndex = this.dragState().removalTargetIndex;

    // Check if dragging back to original position on conveyor (cancel removal)
    if (removalIndex >= 0) {
      const klankRect = this.#conveyorKlankRects[removalIndex];
      if (klankRect) {
        const containerRect = this.mechanicContainer.nativeElement.getBoundingClientRect();
        const isOverOriginalPosition = this.#isPositionInRect(event.pointerPosition, klankRect, { margin: 50 });

        if (isOverOriginalPosition) {
          this.#updateDragState({
            mode: 'hovering-cancel-removal',
            targetIndex: removalIndex,
            position: {
              x: klankRect.left - containerRect.left,
              y: klankRect.top - containerRect.top,
            },
          });
          return;
        }
      }
    }

    // Default removal dragging state
    this.#updateDragState({ mode: 'dragging-removal' });
  }

  dragReleased(event: CdkDragRelease): void {
    // Prevent completing drag if there's already a pending mutation
    if (this.gameState().hasPendingMutation) {
      event.source.reset();
      this.#resetDragState();
      return;
    }

    event.source.reset();

    const state = this.dragState();
    const activeKlank = this.activeDraggableKlank();

    // Clear the dragging animation state
    if (activeKlank) {
      this.#clearKlankAnimationState(activeKlank.uid);
    }

    if (state.mode === 'hovering-replace') {
      this.#commitReplace(state.targetIndex);
    } else if (state.mode === 'hovering-insert') {
      this.#commitInsert(state.targetIndex);
    } else if (state.mode === 'dragging-removal') {
      this.#commitRemoval(state.removalTargetIndex);
    } else if (state.mode === 'hovering-cancel-removal') {
      // Cancel removal - just reset state, no changes needed
      console.log('Removal cancelled - klank returned to original position');
    }

    this.#resetDragState();
  }

  async undoMutation(): Promise<void> {
    const gameState = this.gameState();

    // Clear all animation states
    this.#klankAnimationStates.clear();

    // Reset to original state
    this.#updateGameState({
      klankenOnConveyor: [...gameState.originalKlankenOnConveyor],
      klankenInAir: [...gameState.originalKlankenInAir],
      exitingKlanken: [],
      hasPendingMutation: false,
    });

    this.#audioService.playReset();

    // Update layout after reset
    setTimeout(() => this.#updateConveyorLayoutInfo(), 0);
  }

  openClaw(uid: string): void {
    this.#updateKlankClawState(uid, true);
  }

  closeClaw(uid: string): void {
    this.#updateKlankClawState(uid, false);
  }

  async submitSolution(): Promise<void> {
    // Only allow submission if there's a pending mutation
    if (!this.gameState().hasPendingMutation) {
      return;
    }

    const isCorrect = this.#validateSolution();

    if (!isCorrect) {
      this.#handleIncorrectSolution();
      return;
    }

    await this.#handleCorrectSolution();
  }

  // ========================================
  // UTILITY METHODS FOR TEMPLATE
  // ========================================

  getNextWordData(): LpWoordData | undefined {
    return this.nextWord();
  }

  findKlankDataByUid(uid: string): KlankData | undefined {
    const gameState = this.gameState();
    const allKlanken = [...gameState.klankenInAir, ...gameState.klankenOnConveyor];
    return allKlanken.find((klank) => klank.uid === uid);
  }

  // ========================================
  // PRIVATE METHODS - ANIMATION SYSTEM
  // ========================================

  #computeKlankAnimationState(
    klankData: KlankData,
    context: {
      isInsertPreview: boolean;
      isReplacePreview: boolean;
      isReplaceTarget: boolean;
      isBeingRemoved: boolean;
      hasPendingMutation: boolean;
      gamePhase: GamePhase;
    },
  ): KlankAnimationState {
    const { isInsertPreview, isReplacePreview, isReplaceTarget, isBeingRemoved, hasPendingMutation, gamePhase } = context;

    // Game completion state takes priority
    if (gamePhase === 'completed') {
      return 'completing';
    }

    // Comitted state when klank is conveyor belt klank and mutations aren't allowed
    if (this.gameState().klankenOnConveyor.some((k) => k.uid === klankData.uid) && hasPendingMutation) {
      this.#setKlankAnimationState(klankData.uid, 'comitted');
      return 'comitted';
    }

    // Disabled state when mutations aren't allowed
    if (hasPendingMutation && !isInsertPreview && !isReplacePreview && !isReplaceTarget && !isBeingRemoved) {
      return 'disabled';
    }

    // Drag interaction states
    if (isBeingRemoved) return 'being-removed';
    if (isReplaceTarget) return 'being-replaced';
    if (isInsertPreview || isReplacePreview) return 'preview';

    // Check for existing animation state
    const existingState = this.#klankAnimationStates.get(klankData.uid);
    if (existingState && this.#shouldContinueAnimation(existingState)) {
      return existingState.current;
    }

    return 'normal';
  }

  #shouldContinueAnimation(animationState: AnimationState): boolean {
    const elapsed = Date.now() - animationState.timestamp;
    const config = ANIMATION_CONFIGS[animationState.current];
    return elapsed < config.duration + (config.delay || 0);
  }

  #setKlankAnimationState(uid: string, newState: KlankAnimationState): void {
    const currentState = this.#klankAnimationStates.get(uid);
    const animationState: AnimationState = {
      current: newState,
      previous: currentState?.current || 'normal',
      timestamp: Date.now(),
    };
    this.#klankAnimationStates.set(uid, animationState);
  }

  #clearKlankAnimationState(uid: string): void {
    this.#klankAnimationStates.delete(uid);
  }

  // ========================================
  // PRIVATE METHODS - GAME LOGIC
  // ========================================

  async #initializeGame(): Promise<void> {
    await this.#tutorService.showMechanic('woordpad');
    this.#updateGameKlanken();
    await waitForMillis(1000);
    await this.panelImageComp.audioButton?.play();
    setTimeout(() => this.#updateConveyorLayoutInfo(), 100);
  }

  #updateGameKlanken(): void {
    const currentWordKlanken = this.#createCurrentWordKlanken();
    const availableKlanken = this.#createAvailableKlanken();

    this.#updateGameState({
      klankenOnConveyor: currentWordKlanken,
      klankenInAir: availableKlanken,
      originalKlankenOnConveyor: [...currentWordKlanken],
      originalKlankenInAir: [...availableKlanken],
      hasPendingMutation: false,
      exitingKlanken: [],
    });
  }

  #createCurrentWordKlanken(): KlankData[] {
    const currentWord = this.currentWord();
    const klanken = WordToKlankConverter.breakupWord(currentWord.word);

    return klanken.map((klank, index) => ({
      uid: `${currentWord.word}-${index}`,
      klank,
      clawOpen: true,
    }));
  }

  #createAvailableKlanken(): KlankData[] {
    const currentWordKlanken = WordToKlankConverter.breakupWord(this.currentWord().word);
    const nextWordKlanken = this.nextWord() ? WordToKlankConverter.breakupWord(this.nextWord()!.word) : undefined;

    const allKlanken = WordToKlankConverter.getAllEasyKlanken();
    const correctKlank = nextWordKlanken ? this.#findCorrectKlank(currentWordKlanken, nextWordKlanken) : undefined;

    const availableKlanken = correctKlank ? WordToKlankConverter.getKlankVariantsFor(correctKlank) : allKlanken;
    const shuffledKlanken = sampleRandom([...availableKlanken], 3);

    if (correctKlank) {
      shuffledKlanken.push(correctKlank);
    }

    return shuffle(shuffledKlanken).map((klank, index) => ({
      uid: `option-${klank}-${index}`,
      klank,
      clawOpen: false,
    }));
  }

  #findCorrectKlank(currentWord: string[], nextWord: string[]): string | undefined {
    const freqCurrent = this.#createFrequencyMap(currentWord);
    const freqNext = this.#createFrequencyMap(nextWord);

    for (const letter of Object.keys(freqNext)) {
      if ((freqCurrent[letter] || 0) < freqNext[letter]) {
        return letter;
      }
    }
    return undefined;
  }

  #createFrequencyMap(letters: string[]): Record<string, number> {
    return letters.reduce(
      (freq, letter) => {
        freq[letter] = (freq[letter] || 0) + 1;
        return freq;
      },
      {} as Record<string, number>,
    );
  }

  #validateSolution(): boolean {
    const nextWordText = this.nextWord()?.word;
    if (!nextWordText) {
      console.error('No next word to compare to!');
      return false;
    }

    const currentSolution = this.gameState()
      .klankenOnConveyor.map((klank) => klank.klank)
      .join('');

    return currentSolution === nextWordText;
  }

  #handleIncorrectSolution(): void {
    console.log('Incorrect solution!');
    this.#tutorService.incorrect();
    this.#updateGameKlanken(); // Reset to original state
  }

  async #handleCorrectSolution(): Promise<void> {
    this.#audioService.playCorrect();
    this.#advanceToNextWord();

    if (this.#isGameComplete()) {
      await this.#handleGameCompletion();
      return;
    }

    this.#updateGameKlanken();
    await waitForMillis(1000);

    const wordData = this.nextWord();
    await this.panelImageComp.audioButton?.play(wordData?.pronunciation1?.url, wordData?.word);
  }

  #advanceToNextWord(): void {
    const currentIndex = this.gameState().currentWordIndex;
    if (currentIndex < this.path().length - 1) {
      this.#updateGameState({ currentWordIndex: currentIndex + 1 });
    }
  }

  #isGameComplete(): boolean {
    return this.gameState().currentWordIndex === this.path().length - 1;
  }

  async #handleGameCompletion(): Promise<void> {
    console.log('You have won the game!');

    // Set all klanken to completing animation state
    this.gameState().klankenOnConveyor.forEach((klank) => {
      this.#setKlankAnimationState(klank.uid, 'completing');
    });

    this.#updateGameState({ phase: 'completed' });
    await this.#audioService.playActivityCorrect();
    await waitForMillis(1000);
    this.finished.emit();
  }

  // ========================================
  // PRIVATE METHODS - DRAG & DROP LOGIC
  // ========================================

  #getContainerRelativePosition(pointerPosition: Position): Position {
    const containerRect = this.mechanicContainer.nativeElement.getBoundingClientRect();
    return {
      x: pointerPosition.x - containerRect.left,
      y: pointerPosition.y - containerRect.top,
    };
  }

  #findInsertionZone(position: Position): InsertionZone | undefined {
    return this.#insertionZones.find((zone) => this.#isPositionInInsertionZone(position, zone));
  }

  #findReplaceTarget(pointerPosition: Position): number {
    return this.#conveyorKlankRects.findIndex((rect) => this.#isPositionInRect(pointerPosition, rect));
  }

  #isPositionInInsertionZone(position: Position, zone: InsertionZone): boolean {
    const margin = DEFAULT_DRAG_MARGIN;
    return (
      position.x >= zone.left - margin &&
      position.x <= zone.right + margin &&
      position.y >= zone.y - margin &&
      position.y <= zone.y + KLANK_HEIGHT + margin
    );
  }

  #isPositionInRect(position: Position, rect: DOMRect, options?: DragDetectionOptions): boolean {
    const margin = options?.margin ?? DEFAULT_DRAG_MARGIN;
    return (
      position.x >= rect.left - margin &&
      position.x <= rect.right + margin &&
      position.y >= rect.top - margin &&
      position.y <= rect.bottom + margin
    );
  }

  #commitReplace(targetIndex: number): void {
    if (targetIndex < 0) return;
    if (this.dragState().dragSource !== 'air') return; // Only allow air-to-conveyor replacement

    const activeKlank = this.activeDraggableKlank();
    if (!activeKlank) return;

    const gameState = this.gameState();
    const baseItems = gameState.klankenOnConveyor;
    const elementBeingReplaced = baseItems[targetIndex];

    // Set animation state for the new element
    this.#setKlankAnimationState(activeKlank.uid, 'inserting');

    // Update the conveyor belt with the new element
    const newKlanken = [...baseItems];
    newKlanken[targetIndex] = { ...activeKlank };

    // Add the replaced element to exiting klanken for animation
    const exitingElement: ExitingKlankData = {
      ...elementBeingReplaced,
      originalIndex: targetIndex,
      exitAnimationState: 'exiting' as const,
    };

    this.#updateGameState({
      klankenOnConveyor: newKlanken,
      exitingKlanken: [...gameState.exitingKlanken, exitingElement],
      hasPendingMutation: true,
    });

    this.#removeUsedKlank();
    this.#updateConveyorLayoutInfo();

    // Clear inserting animation after completion
    setTimeout(() => {
      this.#clearKlankAnimationState(activeKlank.uid);
    }, ANIMATION_CONFIGS.inserting.duration);

    // Remove the exiting element after animation completes
    setTimeout(() => {
      const currentExiting = this.gameState().exitingKlanken;
      const filteredExiting = currentExiting.filter((k) => k.uid !== exitingElement.uid);
      this.#updateGameState({ exitingKlanken: filteredExiting });
    }, ANIMATION_CONFIGS.exiting.duration);
  }

  #commitInsert(targetIndex: number): void {
    if (targetIndex < 0) return;
    if (this.dragState().dragSource !== 'air') return; // Only allow air-to-conveyor insertion

    const activeKlank = this.activeDraggableKlank();
    if (!activeKlank) return;

    // Set animation state for the new element
    this.#setKlankAnimationState(activeKlank.uid, 'inserting');

    const newKlanken = [...this.gameState().klankenOnConveyor];
    newKlanken.splice(targetIndex, 0, { ...activeKlank });

    this.#updateGameState({
      klankenOnConveyor: newKlanken,
      hasPendingMutation: true,
    });
    this.#removeUsedKlank();
    setTimeout(() => this.#updateConveyorLayoutInfo(), 0);

    // Clear inserting animation after completion
    setTimeout(() => {
      this.#clearKlankAnimationState(activeKlank.uid);
    }, ANIMATION_CONFIGS.inserting.duration);
  }

  #commitRemoval(targetIndex: number): void {
    if (targetIndex < 0) return;

    const gameState = this.gameState();
    const conveyorKlanken = gameState.klankenOnConveyor;
    const klankToRemove = conveyorKlanken[targetIndex];
    if (!klankToRemove) return;

    // Remove the klank from the conveyor
    const newConveyorKlanken = [...conveyorKlanken];
    newConveyorKlanken.splice(targetIndex, 1);

    // Add the removed klank back to the air
    const newAirKlanken = [...gameState.klankenInAir, klankToRemove];

    this.#updateGameState({
      klankenOnConveyor: newConveyorKlanken,
      klankenInAir: newAirKlanken,
      hasPendingMutation: true,
    });

    setTimeout(() => this.#updateConveyorLayoutInfo(), 0);
  }

  #removeUsedKlank(): void {
    const dragState = this.dragState();
    const activeIndex = dragState.activeDraggableIndex;
    const source = dragState.dragSource;

    if (activeIndex < 0) return;

    if (source === 'air') {
      const newKlanken = [...this.gameState().klankenInAir];
      newKlanken.splice(activeIndex, 1);
      this.#updateGameState({ klankenInAir: newKlanken });
    }
    // For conveyor source, removal is handled in #commitRemoval
  }

  // ========================================
  // PRIVATE METHODS - LAYOUT MANAGEMENT
  // ========================================

  #updateConveyorLayoutInfo(): void {
    const originalItemsLength = this.gameState().klankenOnConveyor.length;
    const klankElements = this.conveyorKlanken.toArray().slice(0, originalItemsLength);

    this.#conveyorKlankRects = klankElements.map((element) => element.nativeElement.getBoundingClientRect());

    this.#calculateInsertionZones();
  }

  #calculateInsertionZones(): void {
    this.#insertionZones = [];

    if (this.#conveyorKlankRects.length === 0) return;

    const containerRect = this.mechanicContainer.nativeElement.getBoundingClientRect();

    // Add all insertion zones
    this.#insertionZones = [
      ...this.#createStartInsertionZone(containerRect),
      ...this.#createMiddleInsertionZones(containerRect),
      ...this.#createEndInsertionZone(containerRect),
    ];
  }

  #createStartInsertionZone(containerRect: DOMRect): InsertionZone[] {
    const firstKlank = this.#conveyorKlankRects[0];
    return [
      {
        left: firstKlank.left - INSERTION_ZONE_WIDTH - containerRect.left,
        right: firstKlank.left - containerRect.left,
        y: firstKlank.top - containerRect.top,
        index: 0,
      },
    ];
  }

  #createMiddleInsertionZones(containerRect: DOMRect): InsertionZone[] {
    const zones: InsertionZone[] = [];

    for (let i = 0; i < this.#conveyorKlankRects.length - 1; i++) {
      const currentKlank = this.#conveyorKlankRects[i];
      const nextKlank = this.#conveyorKlankRects[i + 1];

      zones.push({
        left: currentKlank.right - containerRect.left,
        right: nextKlank.left - containerRect.left,
        y: currentKlank.top - containerRect.top,
        index: i + 1,
      });
    }

    return zones;
  }

  #createEndInsertionZone(containerRect: DOMRect): InsertionZone[] {
    const lastKlank = this.#conveyorKlankRects[this.#conveyorKlankRects.length - 1];
    return [
      {
        left: lastKlank.right - containerRect.left,
        right: lastKlank.right + INSERTION_ZONE_WIDTH - containerRect.left,
        y: lastKlank.top - containerRect.top,
        index: this.#conveyorKlankRects.length,
      },
    ];
  }

  // ========================================
  // PRIVATE METHODS - STATE MANAGEMENT
  // ========================================

  #updateGameState(updates: Partial<GameState>): void {
    this.#gameState.set({ ...this.#gameState(), ...updates });
  }

  #updateDragState(updates: Partial<DragInteractionState>): void {
    this.#dragState.set({ ...this.#dragState(), ...updates });
  }

  #resetDragState(): void {
    this.#updateDragState({
      mode: 'idle',
      targetIndex: -1,
      position: { x: 0, y: 0 },
      activeDraggableIndex: -1,
      dragSource: 'air',
      removalTargetIndex: -1,
    });
  }

  #updateKlankClawState(uid: string, isOpen: boolean): void {
    const klankData = this.findKlankDataByUid(uid);
    if (!klankData) return;

    const wasOpen = klankData.clawOpen;

    if (wasOpen !== isOpen) {
      this.#audioService.playClawGrab();
      if (!isOpen) {
        this.#audioService.playClawMove();
      }
    }

    klankData.clawOpen = isOpen;
  }
}
